import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  Button,
} from "@mui/material";
import GaugeChart from "react-gauge-chart";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import {
  apiCallGetLiveDemoGraphGraph,
  apiCallGetLiveFootTraffic,
} from "../../../Core/Apicall";

/* ================= REGISTER ================= */
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

type DonutItem = {
  label: string;
  value: number;
  color: string;
};

type AnalyticsData = {
  age: DonutItem[];
  gender: DonutItem[];
  groups: DonutItem[];
};

const COLORS = {
  primary: "#2563EB",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
  muted: "#6B7280",
  grid: "#E5E7EB",
  track: "#E5E7EB",
};

/* ================= MAIN ================= */
const VenueAnalyticsDashboard = ({ venueId }: { venueId: string }) => {

  const [data, setData] = useState<AnalyticsData | null>(null);
  const [isHoliday, setIsHoliday] = useState(false);
  const [traffic, setTraffic] = useState<{
    index: number | null;
    label: string;
    comparison: {
      usualPattern: number;
      nearbyVenues: number;
      dayTimeAvg: number;
    };
  } | null>(null);

  useEffect(() => {
    fetchAnalytics();
    fetchTrafficAnalytics();
  }, []);

  const onClickRefresh = () => {
    fetchAnalytics();
    fetchTrafficAnalytics();
  }

  const fetchAnalytics = async () => {
    try {
      const res = await apiCallGetLiveDemoGraphGraph({
        venueId
      });
      setIsHoliday(res.data?.isHoliday === true);
      setData(res.data);
    } catch (error) { }
  };
  const fetchTrafficAnalytics = async () => {
    try {
      const res = await apiCallGetLiveFootTraffic({
        venueId
      });
      setTraffic(res.data);
    } catch (error) { }
  };

  const formatCompare = (value: number) =>
    value === 0 ? null : Math.min(value, 100);

  return (
    <>
      <Box
        sx={{
          filter: isHoliday ? "blur(4px)" : "none",
          pointerEvents: isHoliday ? "none" : "auto",
          transition: "0.3s ease",
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontWeight={700}></Typography>

          <Chip
            label="Refresh"
            clickable
            color="primary"
            onClick={() => onClickRefresh()}
          />
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
          gap={3}
          mb={3}
        >
          {/* Live Foot Traffic Index */}
          <CardBox title="Live Foot Traffic Index" compact>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box sx={{ width: 180, height: 110 }}>
                <GaugeChart
                  id="traffic"
                  percent={traffic?.index ? traffic.index / 100 : 0}
                  nrOfLevels={30}
                  colors={[COLORS.success, COLORS.warning, COLORS.danger]}
                  needleColor="#111"
                  animate={false}
                  formatTextValue={() => ""}
                  style={{ height: "100%" }}
                />
              </Box>

              <Typography fontSize={24} fontWeight={800} mt={-1}>
                {traffic?.index ?? "--"}%
              </Typography>
              <Chip
                label={traffic?.label ?? "Building insights"}
                color={
                  traffic?.index
                    ? traffic.index >= 75
                      ? "error"
                      : traffic.index >= 40
                        ? "warning"
                        : "default"
                    : "default"
                }
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </CardBox>

          {/* Traffic Comparison */}
          <CardBox title="Traffic Comparison" compact>
            <CompareRow
              label="Usual Pattern"
              value={formatCompare(traffic?.comparison.usualPattern ?? 0)}
              hint="Learning"
            />

            <CompareRow
              label="Nearby Venues"
              value={formatCompare(traffic?.comparison.nearbyVenues ?? 0)}
            />

            <CompareRow
              label="Day / Time Avg"
              value={formatCompare(traffic?.comparison.dayTimeAvg ?? 0)}
              hint="Learning"
            />
          </CardBox>

        </Box>

        {/* ================= DEMOGRAPHICS ================= */}
        {data && (
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
            gap={3}
          >
            <DonutCard title="Age Groups" items={data.age} />
            <DonutCard title="Gender Mix" items={data.gender} />
            <DonutCard title="Social Groups" items={data.groups} />
          </Box>
        )}
      </Box>
      <Dialog
        open={isHoliday}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            textAlign: "center",
          },
        }}
      >
        <DialogContent>
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              backgroundColor: "#FEF3C7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
            }}
          >
            🎉
          </Box>

          <Typography fontSize={18} fontWeight={700} mb={1}>
            Holiday Today
          </Typography>

          <Typography fontSize={14} color="text.secondary" mb={3}>
            Today is a holiday at the venue.
            <br />
            Live analytics are temporarily paused.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            sx={{ borderRadius: 2, py: 1 }}
            onClick={() => setIsHoliday(false)}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ================= COMPONENTS ================= */

const CardBox = ({ title, children, compact = false }: any) => (
  <Card sx={{ height: compact ? 280 : "100%" }}>
    <CardContent sx={{ pb: compact ? "12px !important" : undefined }}>
      <Typography fontWeight={600} mb={1}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const CompareRow = ({ label, value }: any) => {
  const isLearning = value === 0 || value === null;

  return (
    <Box mb={1.5}>
      <Typography fontSize={12} color={COLORS.muted}>
        {label}
      </Typography>

      {isLearning ? (
        <>
          <Typography fontSize={11} color="#9CA3AF" fontStyle="italic">
            Learning baseline
          </Typography>
          <Typography fontSize={10} color="#9CA3AF">
            More data needed for this time slot
          </Typography>
        </>
      ) : (
        <>
          <Box height={8} bgcolor={COLORS.track} borderRadius={6}>
            <Box
              width={`${Math.min(value, 100)}%`}
              height="100%"
              bgcolor={COLORS.primary}
              borderRadius={6}
            />
          </Box>
          <Typography fontSize={11}>{value}%</Typography>
        </>
      )}
    </Box>
  );
};


const alwaysVisibleLabels = {
  id: "alwaysVisibleLabels",
  afterDraw(chart: any) {
    const dataset = chart.data.datasets[0];

    // 👉 agar dummy data hai to kuch bhi mat dikhao
    if (dataset.data.length === 1 && dataset.data[0] === 1) return;

    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((element: any, index: number) => {
      const value = dataset.data[index];
      if (!value) return;

      const { x, y } = element.tooltipPosition();
      ctx.save();
      ctx.fillStyle = "#FFF";
      ctx.font = "600 12px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${value}`, x, y);
      ctx.restore();
    });
  },
};

const isAllZero = (items: DonutItem[]) => items.every((i) => i.value === 0);

const emptyCenterText = {
  id: "emptyCenterText",
  beforeDraw(chart: any) {
    const { ctx, chartArea } = chart;

    if (!chartArea) return;

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#9CA3AF";
    ctx.font = "600 10px sans-serif";
    ctx.fillText("Not yet data", centerX, centerY);
    ctx.restore();
  },
};

const DonutCard = ({
  title,
  items = [],
}: {
  title: string;
  items: DonutItem[];
}) => {
  const allZero = isAllZero(items);

  const chartData = useMemo(
    () => ({
      labels: items.map((i) => i.label),
      datasets: [
        {
          data: allZero ? [1] : items.map((i) => i.value),
          backgroundColor: allZero
            ? ["#E5E7EB"] // light grey placeholder ring
            : items.map((i) => i.color),
          borderWidth: 0,
        },
      ],
    }),
    [items, allZero]
  );

  return (
    <Card>
      <CardContent>
        <Typography fontWeight={600} mb={1}>
          {title}
        </Typography>

        <Box height={180}>
          <Doughnut
            data={chartData}
            plugins={allZero ? [emptyCenterText] : [alwaysVisibleLabels]}
            options={{
              maintainAspectRatio: false,
              cutout: "65%",
              plugins: {
                legend: {
                  position: "bottom",
                  labels: {
                    boxWidth: 10,
                    color: "#374151",
                  },
                },
                tooltip: {
                  enabled: !allZero,
                },
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default VenueAnalyticsDashboard;
